import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/AssignRooms.css";
import { formatDate } from "../../utils/date";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { GoldLoader } from "../shared/Loaders";

const pickerDateToKey = (d) => {
  if (!d) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AssignRooms = ({ hotel, room, onClose, onAssigned }) => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [activeTab, setActiveTab] = useState("individual");
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);

  const [individualSelected, setIndividualSelected] = useState([]);
  const [groupMembersSelected, setGroupMembersSelected] = useState([]);

  const [searchIndividuals, setSearchIndividuals] = useState("");
  const [searchGroups, setSearchGroups] = useState("");
  const [numberOfRooms, setNumberOfRooms] = useState(1);
  const [assignedMap, setAssignedMap] = useState({});
  // NEW: accordion edit state
  const [editingPersonId, setEditingPersonId] = useState(null);
  


  /* ================= DATE HELPERS ================= */

  const isWithinRoomRange = (date) => {
    if (!date) return false;
    const fromKey = room?.from_la_date_key;
    const toKey = room?.to_la_date_key;
    if (!fromKey || !toKey) return false;
    const dk = pickerDateToKey(new Date(date));
    return dk >= fromKey && dk <= toKey;
  };

  const dateStyle = (date) => ({
    color: isWithinRoomRange(date) ? "#198754" : "#D9A128",
    padding: "6px 12px",
    borderRadius: "4px",
    minWidth: "110px",
    textAlign: "center",
    display: "inline-block",
  });

  /* ================= ORGANISATION RESOLVER ================= */

  const getOrganisationName = (personId, orgMap, roles) => {
    if (!personId || !Array.isArray(roles)) return "N/A";

    const orgNames = roles
      .filter((role) => {
        const rolePersonId =
          typeof role.person_id === "string"
            ? role.person_id
            : role.person_id?._id;
        return rolePersonId === personId;
      })
      .map((role) => {
        if (typeof role.org_id === "string") return orgMap[role.org_id];
        if (typeof role.org_id === "object")
          return role.org_id?.name || role.org_id?.org_name;
        return null;
      })
      .filter(Boolean);

    return orgNames.length ? [...new Set(orgNames)].join(", ") : "N/A";
  };

  const isAlreadyAssigned = (p) =>
  assignedMap[p.participant_id] ===
  hotel?.global_hotel_id?.global_hotel_name;


  /* ================= FETCH DATA ================= */

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          participantsRes,
          orgRes,
          rolesRes,
          assignedRes,
        ] = await Promise.all([
          axios.get(`${API_URL}/ems/participants`, { params: { slim: true } }),
          axios.get(`${API_URL}/organisations`),
          axios.get(`${API_URL}/personRoles`),
          axios.get(`${API_URL}/ems/assign-rooms`),
        ]);

        const participants =
          participantsRes.data?.data ||
          participantsRes.data?.participants ||
          participantsRes.data ||
          [];

        const roles = rolesRes.data || [];

        const orgMap = orgRes.data.reduce((map, org) => {
          map[org._id] = org.name;
          return map;
        }, {});

        const assigned = {};
        (assignedRes.data || []).forEach((a) => {
          const hotelName =
            a.hotel_id?.global_hotel_id?.global_hotel_name;

          (a.participant_ids || []).forEach((pid) => {
            assigned[String(pid)] = hotelName;
          });
        });
        setAssignedMap(assigned);

        const personsMap = {};

        participants.forEach((p) => {
          if (!p.person_id?._id) return;

          const personId = p.person_id._id;

          let meta = {};
          try {
            meta = p.person_data ? JSON.parse(p.person_data) : {};
          } catch {}

          const groupArr = Array.isArray(p.group_id)
            ? p.group_id
            : p.group_id
            ? [p.group_id]
            : [];

          const hasAthlete = groupArr.some((g) =>
            g?.group_name?.toLowerCase().includes("athlete")
          );
          if (hasAthlete) return;

          const fullName = `${meta.givenFirstName || p.person_id.name || ""} ${
            meta.familyLastName || p.person_id.lname || ""
          }`.trim();

          if (!personsMap[personId]) {
            personsMap[personId] = {
              _id: personId,
              participant_id: p._id,
              fullName,
              organisation: getOrganisationName(personId, orgMap, roles),
              groupNames: [],
              checkInDate: meta.checkInDate
                ? new Date(meta.checkInDate)
                : null,
              checkOutDate: meta.checkOutDate
                ? new Date(meta.checkOutDate)
                : null,
            };
          }

          groupArr.forEach((g) => {
            if (
              g?.group_name &&
              !personsMap[personId].groupNames.includes(g.group_name)
            ) {
              personsMap[personId].groupNames.push(g.group_name);
            }
          });
        });

        setPeople(
          Object.values(personsMap).map((p) => ({
            ...p,
            group: p.groupNames.join(", "),
          }))
        );
      } catch (err) {
        console.error("Failed to fetch participants", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL, room]);

  /* ================= FILTERING ================= */

 

  const filteredIndividuals = people.filter((p) =>
    `${p.fullName} ${p.organisation}`
      .toLowerCase()
      .includes(searchIndividuals.toLowerCase())
  );

  const filteredGroupMembers = people.filter((p) =>
    `${p.fullName} ${p.organisation} ${p.group}`
      .toLowerCase()
      .includes(searchGroups.toLowerCase())
  );

  /* ================= HANDLERS ================= */

  const toggleIndividual = (id) =>
    setIndividualSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleGroupMember = (id) =>
    setGroupMembersSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // NEW: edit date handler
  const handleDateChange = (personId, field, value) => {
    setPeople((prev) =>
      prev.map((p) =>
        p._id === personId ? { ...p, [field]: value } : p
      )
    );
  };

  /* ================= UPDATE DATES HANDLER ================= */
  const handleUpdateDates = async (p) => {
  // 1. Validation
  if (p.checkInDate && p.checkOutDate && p.checkOutDate < p.checkInDate) {
    alert(`Invalid dates: Check-out cannot be before Check-in for ${p.fullName}`);
    return;
  }

  try {
    // 2. Fetch latest participant data to ensure we don't overwrite other fields in person_data
    const res = await axios.get(`${API_URL}/ems/participants/${p.participant_id}`);
    
    const existingPersonData = typeof res.data.person_data === "string" 
      ? JSON.parse(res.data.person_data || "{}") 
      : res.data.person_data || {};

    // 3. Prepare the updated payload
    const updatedPersonData = {
      ...existingPersonData,
      checkInDate: p.checkInDate ? pickerDateToKey(p.checkInDate) : null,
      checkOutDate: p.checkOutDate ? pickerDateToKey(p.checkOutDate) : null,
    };

    // 4. Update the Database (always send object, never string)
    await axios.put(`${API_URL}/ems/participants/${p.participant_id}`, {
      person_data: updatedPersonData,
    });

    // 5. Success Feedback
    setEditingPersonId(null);
    alert(`Dates successfully updated in database for ${p.fullName}`);
    
  } catch (err) {
    console.error("Database update failed:", err);
    alert("Failed to update dates in the database. Please try again.");
  }
};
  



  const handleAssign = async () => {
  const selectedPeople = individualSelected.length
    ? people.filter((p) => individualSelected.includes(p._id))
    : people;

  for (const p of selectedPeople) {
    if (
      p.checkInDate &&
      p.checkOutDate &&
      p.checkOutDate < p.checkInDate
    ) {
      alert(`Invalid dates for ${p.fullName}`);
      return;
    }
  }

  try {
    // 🔁 UPDATE participant.person_data (merge-safe)
    await Promise.all(
      selectedPeople.map(async (p) => {
        const res = await axios.get(
          `${API_URL}/ems/participants/${p.participant_id}`
        );

        const existingPersonData =
          typeof res.data.person_data === "string"
            ? JSON.parse(res.data.person_data || "{}")
            : res.data.person_data || {};

        const updatedPersonData = {
          ...existingPersonData,
          checkInDate: p.checkInDate
            ? pickerDateToKey(p.checkInDate)
            : null,
          checkOutDate: p.checkOutDate
            ? pickerDateToKey(p.checkOutDate)
            : null,
        };

        return axios.put(
          `${API_URL}/ems/participants/${p.participant_id}`,
          {
            person_data: updatedPersonData,
          }
        );
      })
    );

    await axios.post(`${API_URL}/ems/assign-rooms`, {
      room_id: room._id,
      hotel_id: hotel._id,
      number_of_rooms: numberOfRooms,
      participant_ids: selectedPeople.map((p) => p.participant_id),
      participant_dates: selectedPeople.map((p) => ({
        participant_id: p.participant_id,
        check_in_date: pickerDateToKey(p.checkInDate),
        check_out_date: pickerDateToKey(p.checkOutDate),
      })),
    });

    onAssigned();
    onClose();
  } catch (err) {
    console.error(err);
    alert("Failed to assign rooms");
  }
};




  return (
    <div className="modal show d-block assign-modal" >
      <div className="modal-dialog modal-xl" >
        <div className="modal-content">
          <div className="modal-header">
            <h5>Assign Rooms</h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {loading ? (
              <GoldLoader label="Loading..." variant="section" />
            ) : (
            <>
            <div className="room-info-card mb-3">
              <div className="fw-semibold mb-2">Room Information</div>

              <div className="d-flex justify-content-between">
                <div>
                  {formatDate(room.from_date)} to {formatDate(room.to_date)}
                </div>
                <div>Available: {room.no_of_rooms}</div>
              </div>
            </div>

            <div className="mb-3">
              <label className="fw-semibold">Number of Rooms</label>
              <input
                type="number"
                min={1}
                max={room.no_of_rooms}
                value={numberOfRooms}
                onChange={(e) => setNumberOfRooms(+e.target.value || 1)}
                className="form-control"
                style={{ maxWidth: 160 }}
              />
            </div>

            <div className="mb-3">
              <button
                className={`btn me-2 ${
                  activeTab === "individual"
                    ? "btn-secondary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => setActiveTab("individual")}
              >
                Individuals
              </button>
              <button
                className={`btn ${
                  activeTab === "group"
                    ? "btn-secondary"
                    : "btn-outline-secondary"
                }`}
                onClick={() => setActiveTab("group")}
              >
                Groups
              </button>
            </div>

            <input
              className="form-control mb-3"
              placeholder="Search by name or organisation"
              value={
                activeTab === "individual"
                  ? searchIndividuals
                  : searchGroups
              }
              onChange={(e) =>
                activeTab === "individual"
                  ? setSearchIndividuals(e.target.value)
                  : setSearchGroups(e.target.value)
              }
            />

            <div className="table-responsive" >
              <table className="table table-striped table-hover mb-3" >
                <thead>
      <tr>
        <th></th>
        <th>Name</th>
        {activeTab === "group" && <th>Group</th>}
        <th>Arrival Date</th>
        <th>Departure Date</th>
        <th>Actions</th>
      </tr>
    </thead>
                <tbody>
                  {(activeTab === "individual"
                    ? filteredIndividuals
                    : filteredGroupMembers
                  ).map((p) => (
                    <React.Fragment key={p._id}>
                      <tr>
                        <td>
                          <input
                          type="checkbox"
                          checked={
                            isAlreadyAssigned(p) ||
                            (activeTab === "individual"
                              ? individualSelected.includes(p._id)
                              : groupMembersSelected.includes(p._id))
                          }
                          onChange={() =>
                            activeTab === "individual"
                              ? toggleIndividual(p._id)
                              : toggleGroupMember(p._id)
                          }
                        />

                        </td>
                        <td>
                          {p.fullName}
                          <div className="text-muted small">
                            {p.organisation || "—"}
                          </div>
                        </td>
                        {activeTab === "group" && <td>{p.group || "—"}</td>}
                        <td>
                          <span style={dateStyle(p.checkInDate)}>
                            {p.checkInDate
                              ? formatDate(p.checkInDate)
                              : "—"}
                          </span>
                        </td>
                        <td>
                          <span style={dateStyle(p.checkOutDate)}>
                            {p.checkOutDate
                              ? formatDate(p.checkOutDate)
                              : "—"}
                          </span>
                        </td>
                        <td>
  <span
    onClick={() =>
      setEditingPersonId(
        editingPersonId === p._id ? null : p._id
      )
    }
    style={{
      cursor: "pointer",
      color: "#000",
      fontSize: "12px",
    }}
    title={editingPersonId === p._id ? "Close" : "Edit Check-in/Check-out date"}
  >
    <FontAwesomeIcon icon={faPen} />
  </span>
</td>

                      </tr>

                       {editingPersonId === p._id && (
                      <tr className="bg-light">
                        <td colSpan={6}>
                          <div className="row g-4 p-3">
                            <div className="col-md-4 px-3">
                              <label className="mb-2">Check-in</label>
                              <DatePicker
  selected={p.checkInDate}
  onChange={(date) =>
    handleDateChange(p._id, "checkInDate", date)
  }
  dateFormat="dd-MMM-yyyy"
  className="form-control"
  showMonthDropdown
  showYearDropdown
  dropdownMode="select"
  // hideIcon
  // toggleCalendarOnIconClick
  onChangeRaw={(e) => e.preventDefault()}
/>

                            </div>

                            <div className="col-md-4 px-3">
                              <label className="mb-2">Check-out</label>
                              <DatePicker
  selected={p.checkOutDate}
  onChange={(date) =>
    handleDateChange(p._id, "checkOutDate", date)
  }
  dateFormat="dd-MMM-yyyy"
  className="form-control"
  showMonthDropdown
  showYearDropdown
  dropdownMode="select"
  // hideIcon
  // toggleCalendarOnIconClick
  onChangeRaw={(e) => e.preventDefault()}
/>

                            </div >
                            <div className="col-md-1 px-3" style={{marginTop:"49px"}}>
                              <button className="btn btn-warning" onClick={() => handleUpdateDates(p)}>Update</button>
                          </div>
                          </div>
                        </td>
                      

                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-warning" onClick={handleAssign}>
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignRooms;
